// Phase SA-4/2 — Slip Autopsy secondary sidebar.
//
// Mirrors the SIKA-5g SikaChatSidebar pattern. Sits between the
// AppShell global sidebar and the autopsy content column. Always
// visible on desktop, collapses to a 52px icon rail.
//
// Layout (on /slips/autopsy):
//   [ AppShell sidebar ] [ Autopsy secondary sidebar ] [ content ]
//
// Six nav rows mapped from the brief §02 surfaces:
//   1. + New autopsy            scroll to Section B (or `onNewAutopsy`)
//   2. 🎯 Profile               active surface during SA-4
//   3. 🛠 Run autopsy           DISABLED stub — SA-5 wires the form
//   4. 📂 History               DISABLED stub — SA-8 wires the list
//   5. 📌 Patterns              DISABLED stub — SA-7/8 wires the filter
//   6. 📊 Compare to STRIKZA    DISABLED stub — SA-9 wires the Pro gate
//
// CSS: this component reuses the SIKA-5g chrome by applying BOTH
// `sika-chat-sidebar` (matches the existing 62 `.scs-*` rules in
// super-acca.css) AND `sa-secondary-sidebar` (the SA-3 alias).
// SA-9 cleanup generalizes the CSS to a single shared class.

import { Link } from "react-router-dom";

import { Icon } from "../../components/icons/Icon.tsx";

export type SlipAutopsyView = "profile" | "tool" | "history" | "patterns";

export interface SlipAutopsySidebarProps {
  /** Which surface is the active one in the content column. SA-4
   * ships only `profile`; SA-5 + SA-8 add `tool` + `history`. */
  activeView?: SlipAutopsyView;
  /** Click handler for `+ New autopsy`. Parent scrolls to Section
   * B + (later) focuses the form. */
  onNewAutopsy?: () => void;
  /** Click handler for `🎯 Profile`. Parent scrolls to Section A. */
  onSelectProfile?: () => void;
  /** Click handler for `🛠 Run autopsy`. Parent scrolls to Section B. */
  onSelectTool?: () => void;
  /** Click handler for `📂 History`. Parent scrolls to Section C. */
  onSelectHistory?: () => void;
  /** User chip — email + tier pill. */
  userEmail?: string | null;
  pro?: boolean;
  /** Collapsed state (persisted by parent to localStorage). */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Mobile drawer open state. */
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
}

export function SlipAutopsySidebar({
  activeView = "profile",
  onNewAutopsy,
  onSelectProfile,
  onSelectTool,
  onSelectHistory,
  userEmail = null,
  pro = false,
  collapsed = false,
  onToggleCollapse,
  mobileOpen = false,
  onCloseMobile,
}: SlipAutopsySidebarProps): JSX.Element {
  const userInitial = userEmail?.charAt(0).toUpperCase() ?? "U";

  return (
    <aside
      // Dual class — see top-of-file note. SA-9 cleanup drops the
      // `sika-chat-sidebar` half once the CSS is generalized.
      className="sa-secondary-sidebar sika-chat-sidebar"
      data-testid="slip-autopsy-sidebar"
      data-collapsed={collapsed || undefined}
      data-mobile-open={mobileOpen || undefined}
      aria-label="Slip Autopsy navigation"
    >
      {/* Brand */}
      <div className="scs-brand">
        <div className="scs-brand-mark" aria-hidden>
          A
        </div>
        <div className="scs-brand-name">Autopsy</div>
        <button
          type="button"
          className="scs-collapse-btn"
          onClick={onToggleCollapse}
          data-testid="slip-autopsy-collapse-btn"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand" : "Collapse"}
        >
          <Icon name={collapsed ? "chev-right" : "chev-left"} size={14} />
        </button>
      </div>

      {/* Scrollable nav body */}
      <div className="scs-scroll">
        {/* 1. + New autopsy */}
        <button
          type="button"
          className="scs-nav-row"
          onClick={() => {
            onNewAutopsy?.();
            onCloseMobile?.();
          }}
          data-testid="sla-nav-new"
        >
          <span className="scs-nav-icon" aria-hidden>
            <Icon name="plus" size={16} />
          </span>
          <span className="scs-nav-label">New autopsy</span>
        </button>

        {/* 2. 🎯 Profile (active) */}
        <button
          type="button"
          className="scs-nav-row"
          data-active={activeView === "profile" || undefined}
          onClick={() => {
            onSelectProfile?.();
            onCloseMobile?.();
          }}
          data-testid="sla-nav-profile"
        >
          <span className="scs-nav-icon" aria-hidden>
            <Icon name="target" size={16} />
          </span>
          <span className="scs-nav-label">Profile</span>
        </button>

        {/* 3. 🛠 Run autopsy (active in SA-5+, scrollable to Section B
                 in SA-4 since the section exists as a placeholder) */}
        <button
          type="button"
          className="scs-nav-row"
          data-active={activeView === "tool" || undefined}
          onClick={() => {
            onSelectTool?.();
            onCloseMobile?.();
          }}
          data-testid="sla-nav-tool"
        >
          <span className="scs-nav-icon" aria-hidden>
            <Icon name="bolt" size={16} />
          </span>
          <span className="scs-nav-label">Run autopsy</span>
        </button>

        {/* 4. 📂 History */}
        <button
          type="button"
          className="scs-nav-row"
          data-active={activeView === "history" || undefined}
          onClick={() => {
            onSelectHistory?.();
            onCloseMobile?.();
          }}
          data-testid="sla-nav-history"
        >
          <span className="scs-nav-icon" aria-hidden>
            <Icon name="clock" size={16} />
          </span>
          <span className="scs-nav-label">History</span>
        </button>

        {/* 5. 📌 Patterns — disabled stub, wired in SA-7/8 polish */}
        <button
          type="button"
          className="scs-nav-row"
          aria-disabled="true"
          disabled
          data-testid="sla-nav-patterns"
          title="Patterns view — coming in SA-8"
        >
          <span className="scs-nav-icon" aria-hidden>
            <Icon name="star" size={16} />
          </span>
          <span className="scs-nav-label">Patterns</span>
        </button>

        {/* 6. 📊 Compare to STRIKZA — Pro gate, wired in SA-9 */}
        <button
          type="button"
          className="scs-nav-row"
          aria-disabled="true"
          disabled
          data-testid="sla-nav-compare"
          title="Compare to STRIKZA — Pro gate ships in SA-9"
        >
          <span className="scs-nav-icon" aria-hidden>
            <Icon name="chart" size={16} />
          </span>
          <span className="scs-nav-label">Compare</span>
        </button>

        {/* Section: Resources — links out to related STRIKZA surfaces.
            Always visible; reinforces the cross-product loop. */}
        <div className="scs-section-head">Related</div>

        <Link
          to="/sika"
          className="scs-nav-row"
          data-testid="sla-nav-sika"
          onClick={onCloseMobile}
        >
          <span className="scs-nav-icon" aria-hidden>
            <Icon name="bubble" size={16} />
          </span>
          <span className="scs-nav-label">Ask SIKA</span>
        </Link>

        <Link
          to="/vault"
          className="scs-nav-row"
          data-testid="sla-nav-vault"
          onClick={onCloseMobile}
        >
          <span className="scs-nav-icon" aria-hidden>
            <Icon name="shield" size={16} />
          </span>
          <span className="scs-nav-label">Vault</span>
        </Link>
      </div>

      {/* User chip */}
      <div className="scs-user-chip" data-testid="sla-user-chip">
        <div className="scs-user-avatar" aria-hidden>
          {userInitial}
        </div>
        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <div className="scs-user-name">{userEmail ?? "Guest"}</div>
          <div className="scs-user-tier" data-tier={pro ? "pro" : "free"}>
            {pro ? "SIKA PRO" : "STRIKZA"}
          </div>
        </div>
      </div>
    </aside>
  );
}
